/**
 * 数据管理 - 抓取状态 + 历史 + 手动触发
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Database, RefreshCw, CheckCircle, XCircle, Clock, Zap, Download, Trash2,
  Plus, Upload, Edit3, X
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { api } from '../../lib/api.js';

export function DataView() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [history, setHistory] = useState([]);
  const [totalPeriods, setTotalPeriods] = useState(0);
  const [showAddPeriod, setShowAddPeriod] = useState(false);
  const [showBatchImport, setShowBatchImport] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [l, h] = await Promise.all([
        api.admin.fetchLogs({ pageSize: 30 }),
        api.history(20, 0),
      ]);
      setLogs(l.logs || []);
      setHistory(h.periods || []);
      setTotalPeriods(h.total || 0);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function doRefresh() {
    setRefreshing(true);
    try {
      const res = await api.refresh();
      if (res.newCount > 0) {
        toast.success(`Worker 抓取:新增 ${res.newCount} 期 (源:${res.source})`);
      } else {
        toast.error(`Worker 抓取失败: ${res.error || '无新数据'} — 试浏览器抓取`);
      }
      load();
    } catch (e) {
      toast.error('Worker 抓取出错: ' + e.message + ' — 试浏览器抓取');
    } finally {
      setRefreshing(false);
    }
  }

  async function doBrowserFetch() {
    setRefreshing(true);
    try {
      const { browserFetchAndImport } = await import('../../lib/browserFetchSsq.js');
      const res = await browserFetchAndImport();
      if (res.ok) {
        toast.success(`浏览器抓取:解析 ${res.parsed} 期 / 入库 +${res.inserted} (源:${res.source})`);
      } else {
        toast.error('浏览器抓取失败: ' + (res.error || '未知'));
      }
      load();
    } catch (e) {
      toast.error('浏览器抓取出错: ' + e.message);
    } finally {
      setRefreshing(false);
    }
  }

  // 统计
  const lastOk = logs.find(l => l.status === 'ok');
  const lastFailed = logs.find(l => l.status === 'failed');
  const successRate = logs.length > 0
    ? (logs.filter(l => l.status === 'ok').length / logs.length * 100).toFixed(1)
    : '0.0';
  const newToday = logs
    .filter(l => l.started_at >= Date.now() - 86400000)
    .reduce((s, l) => s + (l.new_periods || 0), 0);

  return (
    <div className="space-y-2">
      <AnimatePresence>
        {showAddPeriod && <AddPeriodModal onClose={() => setShowAddPeriod(false)} onSuccess={load} />}
        {showBatchImport && <BatchImportModal onClose={() => setShowBatchImport(false)} onSuccess={load} />}
      </AnimatePresence>
      {/* 状态卡片 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <StatusCard
          icon={Database}
          label="总期数"
          value={totalPeriods.toLocaleString()}
          color="text-brand-cyan"
        />
        <StatusCard
          icon={CheckCircle}
          label="最近成功"
          value={lastOk ? new Date(lastOk.started_at).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '无'}
          color="text-brand-cyan"
        />
        <StatusCard
          icon={XCircle}
          label="最近失败"
          value={lastFailed ? new Date(lastFailed.started_at).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '无'}
          color={lastFailed ? 'text-accent-danger' : 'text-ink-3'}
        />
        <StatusCard
          icon={Zap}
          label="成功率"
          value={`${successRate}%`}
          color={successRate >= 80 ? 'text-brand-cyan' : 'text-brand-gold'}
          sub={`今日新增 ${newToday} 期`}
        />
      </div>

      {/* 操作栏 */}
      <div className="card !p-2.5 flex items-center justify-between">
        <div>
          <h3 className="text-xs font-semibold flex items-center gap-1.5">
            <Database size={12} className="text-brand-cyan" />
            数据抓取
          </h3>
          <p className="text-[10px] text-ink-3 mt-0.5">
            自动:每天 22:00 (UTC 14:00) · 支持手动触发
          </p>
        </div>
        <div className="flex gap-1.5">
          <button
            onClick={() => setShowAddPeriod(true)}
            className="btn-ghost text-[10px] flex items-center gap-1 py-1 px-2"
          >
            <Plus size={10} />
            加单期
          </button>
          <button
            onClick={() => setShowBatchImport(true)}
            className="btn-ghost text-[10px] flex items-center gap-1 py-1 px-2"
          >
            <Upload size={10} />
            批量导入
          </button>
          <button
            onClick={doRefresh}
            disabled={refreshing}
            className="btn-ghost text-[10px] flex items-center gap-1 py-1 px-2"
            title="Worker 抓取(可能被目标站 IP 屏蔽)"
          >
            <RefreshCw size={10} className={refreshing ? 'animate-spin' : ''} />
            Worker 抓取
          </button>
          <button
            onClick={doBrowserFetch}
            disabled={refreshing}
            className="btn-primary text-[11px] flex items-center gap-1 py-1 px-2.5"
            title="用你的浏览器 IP 抓(绕开 Worker 屏蔽)"
          >
            <Zap size={11} />
            浏览器抓取
          </button>
        </div>
      </div>

      {/* 抓取历史 */}
      <div className="card !p-0 overflow-hidden">
        <div className="px-2.5 py-1.5 border-b border-white/5 flex items-center justify-between">
          <h3 className="text-xs font-semibold">抓取历史</h3>
          <span className="text-[10px] text-ink-3">最近 {logs.length} 次</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-white/2 text-[10px] text-ink-3">
              <tr>
                <th className="text-left py-1.5 px-2.5 font-medium">时间</th>
                <th className="text-left py-1.5 px-2.5 font-medium">状态</th>
                <th className="text-left py-1.5 px-2.5 font-medium">来源</th>
                <th className="text-right py-1.5 px-2.5 font-medium">新增</th>
                <th className="text-left py-1.5 px-2.5 font-medium">错误</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(l => (
                <tr key={l.id} className="border-t border-white/3 hover:bg-white/3">
                  <td className="py-1.5 px-2.5 text-[11px] text-ink-3 font-mono">
                    {new Date(l.started_at).toLocaleString('zh-CN', { hour12: false })}
                  </td>
                  <td className="py-1.5 px-2.5">
                    <span className={clsx(
                      'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium',
                      l.status === 'ok' ? 'bg-brand-cyan/20 text-brand-cyan' :
                      l.status === 'failed' ? 'bg-accent-danger/20 text-accent-danger' :
                      'bg-white/10 text-ink-3'
                    )}>
                      {l.status === 'ok' && <CheckCircle size={9} />}
                      {l.status === 'failed' && <XCircle size={9} />}
                      {l.status === 'running' && <Clock size={9} className="animate-pulse" />}
                      {l.status}
                    </span>
                  </td>
                  <td className="py-1.5 px-2.5 text-[11px]">{l.source}</td>
                  <td className="py-1.5 px-2.5 text-right font-mono">
                    {l.new_periods > 0 ? <span className="text-brand-cyan">+{l.new_periods}</span> : 0}
                  </td>
                  <td className="py-1.5 px-2.5 text-[11px] text-accent-danger max-w-xs truncate">
                    {l.error || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {logs.length === 0 && !loading && (
            <div className="text-center py-4 text-xs text-ink-3">暂无抓取记录</div>
          )}
        </div>
      </div>

      {/* 最近数据预览 */}
      <div className="card !p-2.5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold">最新数据(前 20 期)</h3>
          <span className="text-[10px] text-ink-3">共 {totalPeriods.toLocaleString()} 期</span>
        </div>
        <div className="grid grid-cols-5 sm:grid-cols-10 gap-1">
          {history.map(p => (
            <div key={p.period} className="text-center p-1 rounded bg-white/3 border border-white/5">
              <div className="text-[8px] text-ink-3 font-mono">{p.period}</div>
              <div className="text-[11px] font-mono font-bold text-brand-cyan">
                {p.a}{p.b}{p.c}
              </div>
            </div>
          ))}
        </div>
        {history.length === 0 && (
          <div className="text-center py-3 text-[10px] text-ink-3">暂无数据</div>
        )}
      </div>
    </div>
  );
}

function StatusCard({ icon: Icon, label, value, color, sub }) {
  return (
    <motion.div whileHover={{ y: -2 }} className="card !p-2.5">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] text-ink-3">{label}</div>
        <Icon size={12} className={color} />
      </div>
      <div className={clsx('text-base font-bold font-mono', color)}>{value}</div>
      {sub && <div className="text-[9px] text-ink-3 mt-0.5">{sub}</div>}
    </motion.div>
  );
}

// 单期添加弹窗
function AddPeriodModal({ onClose, onSuccess }) {
  const [period, setPeriod] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [c, setC] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!period || !date || a === '' || b === '' || c === '') {
      toast.error('请填完整');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.admin.addPeriod({
        period, date, a: Number(a), b: Number(b), c: Number(c), source: 'manual'
      });
      if (res?.ok) {
        toast.success('已添加');
        onSuccess();
        onClose();
      } else {
        toast.error(res?.error || '失败');
      }
    } catch (e) { toast.error(e.message); }
    setSubmitting(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 30 }} animate={{ y: 0 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-md glass-strong rounded-2xl p-5"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold flex items-center gap-2">
            <Plus size={16} className="text-brand-cyan" />
            添加单期数据
          </h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="space-y-2.5">
          <div>
            <label className="text-xs text-ink-2 mb-1 block">期号(7 位)</label>
            <input
              type="text"
              value={period}
              onChange={e => setPeriod(e.target.value)}
              maxLength={7}
              placeholder="2026201"
              className="w-full bg-bg-card border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-brand-cyan/40"
            />
          </div>
          <div>
            <label className="text-xs text-ink-2 mb-1 block">开奖日期</label>
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              className="w-full bg-bg-card border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-brand-cyan/40"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[['a', a, setA], ['b', b, setB], ['c', c, setC]].map(([k, v, s]) => (
              <div key={k}>
                <label className="text-xs text-ink-2 mb-1 block">{k.toUpperCase()} 位(0-9)</label>
                <input
                  type="number"
                  value={v}
                  onChange={e => s(e.target.value.replace(/\D/g, '').slice(0, 1))}
                  min={0}
                  max={9}
                  placeholder="0-9"
                  className="w-full bg-bg-card border border-white/10 rounded-lg px-2 py-2 text-center text-lg font-mono outline-none focus:border-brand-cyan/40"
                />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <button onClick={onClose} className="btn-secondary text-sm">取消</button>
            <button onClick={handleSubmit} disabled={submitting} className="btn-primary text-sm">
              {submitting ? '提交中...' : '添加'}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// 批量导入弹窗
function BatchImportModal({ onClose, onSuccess }) {
  const [text, setText] = useState('');
  const [format, setFormat] = useState('json');  // 'json' | 'csv'
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!text.trim()) { toast.error('请粘贴数据'); return; }
    let periods = [];
    try {
      if (format === 'json') {
        const parsed = JSON.parse(text);
        periods = Array.isArray(parsed) ? parsed : [parsed];
      } else {
        // CSV: period,date,a,b,c
        periods = text.trim().split('\n').map(line => {
          const [period, date, a, b, c] = line.split(',').map(s => s.trim());
          return { period, date, a: Number(a), b: Number(b), c: Number(c) };
        });
      }
    } catch (e) {
      toast.error('解析失败: ' + e.message);
      return;
    }
    if (periods.length === 0) { toast.error('没数据'); return; }
    setSubmitting(true);
    try {
      const res = await api.admin.batchImportPeriods(periods);
      if (res?.ok) {
        toast.success(res.message);
        onSuccess();
        onClose();
      } else {
        toast.error(res?.error || '失败');
      }
    } catch (e) { toast.error(e.message); }
    setSubmitting(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 30 }} animate={{ y: 0 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-lg glass-strong rounded-2xl p-5"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold flex items-center gap-2">
            <Upload size={16} className="text-brand-cyan" />
            批量导入数据
          </h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="space-y-2.5">
          <div className="grid grid-cols-2 gap-1.5">
            <button
              onClick={() => setFormat('json')}
              className={clsx(
                'py-1.5 rounded-lg text-xs',
                format === 'json' ? 'bg-brand-cyan/30 text-brand-cyan border border-brand-cyan/40' : 'bg-white/5 text-ink-2'
              )}
            >
              JSON 格式
            </button>
            <button
              onClick={() => setFormat('csv')}
              className={clsx(
                'py-1.5 rounded-lg text-xs',
                format === 'csv' ? 'bg-brand-cyan/30 text-brand-cyan border border-brand-cyan/40' : 'bg-white/5 text-ink-2'
              )}
            >
              CSV 格式
            </button>
          </div>
          <div className="rounded-lg bg-bg-card border border-white/10 p-2 text-[10px] text-ink-2 font-mono">
            {format === 'json' ? (
              <pre>{`[
  {"period":"2026201","date":"2026-08-10","a":5,"b":6,"c":1},
  {"period":"2026202","date":"2026-08-11","a":2,"b":3,"c":4}
]`}</pre>
            ) : (
              <pre>{`2026201,2026-08-10,5,6,1
2026202,2026-08-11,2,3,4
2026203,2026-08-12,9,8,7`}</pre>
            )}
          </div>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            rows={8}
            placeholder={`粘贴 JSON 或 CSV 数据...`}
            className="w-full bg-bg-card border border-white/10 rounded-lg px-3 py-2 text-xs font-mono outline-none focus:border-brand-cyan/40 resize-none"
          />
          <div className="grid grid-cols-2 gap-2 pt-1">
            <button onClick={onClose} className="btn-secondary text-sm">取消</button>
            <button onClick={handleSubmit} disabled={submitting} className="btn-primary text-sm">
              {submitting ? '导入中...' : '导入'}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
